import { animate, group, query, state, style, transition, trigger } from '@angular/animations';
import { AsyncPipe, NgClass, NgStyle, TitleCasePipe } from '@angular/common';
import { Component, inject, Input, OnChanges, SimpleChange, SimpleChanges } from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Backpack, LucideAngularModule } from 'lucide-angular';
import { filter, Observable } from 'rxjs';
import { ClientModel } from '../../models/client.model';
import { selectClient, selectClientTokensLeft } from '../../store/client/client.selects';
import { Store } from '@ngrx/store';
import { selectJobsByStatusCount } from '../../store/jobs/jobs.selects';
import { RoundToKPipe } from '../../pipes/round-to-k.pipe';

export type MenuItem = { name: string, route: string, icon: string, active: boolean }

const animationDutation: string = '270ms ease-in-out';
const classToAnimate: string = '.menu-item p, .social, .client-menu-data, .chevron-down';
const closeMenuSize: string = '80px';
const openMenuSize: string = '240px';

@Component({
  selector: 'app-side-bar',
  imports: [LucideAngularModule, AsyncPipe, NgClass, NgStyle, TranslateModule, TitleCasePipe, RouterLink, RoundToKPipe],
  templateUrl: './side-bar.component.html',
  styleUrls: ['./side-bar.component.scss'],
  animations: [
    trigger('menuAnimation', [
      state('open', style({ width: openMenuSize })),
      state('close', style({ width: closeMenuSize })),

      transition('open => close', [
        group([
          query(':self', [
            animate(animationDutation, style({ width: closeMenuSize }))
          ]),
          query(classToAnimate, [
            style({ opacity: 1 }),
            animate(animationDutation, style({ opacity: 0 })),
            style({ opacity: 0 }),
          ], { optional: true })
        ])
      ]),

      transition('close => open', [
        group([
          query(':self', [
            animate(animationDutation, style({ width: openMenuSize }))
          ]),
          query(classToAnimate, [
            style({ opacity: 0 }),
            animate(animationDutation, style({ opacity: 1 })),
            style({ opacity: 1 }),
          ], { optional: true })
        ])
      ])
    ])
  ]
})


export class SideBarComponent {

  private store: Store = inject(Store);
  private router: Router = inject(Router);

  protected menuState: 'open' | 'close' = 'close';
  client$: Observable<ClientModel | null> = this.store.select(selectClient);
  tokensLeft$: Observable<number> = this.store.select(selectClientTokensLeft);

  notificationsCount$: Observable<number> = this.store.select(selectJobsByStatusCount('WAITING_RESPONSE'));

  // Colors 
  light300: string = '#919A96'
  light: string = '#EFF9F5'
  currentRoute!: string;
  menuItems: MenuItem[] = [
    {
      name: 'dashboard',
      route: '/dashboard',
      icon: 'home',
      active: false,
    },
    {
      name: 'upload',
      route: '/dashboard/create-job',
      icon: 'plus',
      active: false,
    },
    {
      name: 'my_videos',
      route: '/dashboard/my-jobs',
      icon: 'videotape',
      active: false,
    },
    {
      name: 'notifications',
      route: '/dashboard/notifs',
      icon: 'bell',
      active: false,
    },
  ];

  bottomMenuItems: MenuItem[] = [
    {
      name: 'about',
      route: '/dashboard/about',
      icon: 'message-circle-heart',
      active: false,
    },
  ];

  toggleMenuState(menuState: 'open' | 'close'): void {
    this.menuState = menuState;
  }

  updateActiveItems(path: string): void {
    this.menuItems = this.menuItems.map((item) => ({
      ...item,
      active: path === (item.route)
    }));
  }

  ngOnInit(): void {

    this.currentRoute = this.router.url;
    this.updateActiveItems(this.currentRoute);

    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd)
    ).subscribe((event) => {
      this.currentRoute = event.urlAfterRedirects;
      this.updateActiveItems(this.currentRoute);
    });
  }
}
